import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ReassignClientsPreviewDto, ReassignSelectionDto } from '@jalupro/shared';
import { api, useApi, useMutation } from '../../api';
import type { ReassignPreviewView, UserView } from '../../api';
import { ErrorState, LoadingState } from '../../components';
import { useT, useLang } from '../../i18n';
import dict from './ReassignClients.i18n';

/**
 * Admin · Reassign clients — bulk move of an agent's clients to another agent
 * (Super Admin, Sales Manager). Selection: all · by province · by city · with orders in
 * the last 4 months · one by one. A preview always comes before the confirmation.
 * Open orders (and their commission) stay with the old agent.
 */
const RECENT_MONTHS = 4;
type Mode = ReassignSelectionDto['mode'];

export default function ReassignClients() {
  const t = useT(dict);
  const { lang } = useLang();
  const navigate = useNavigate();
  const agentsReq = useApi(() => api.agents.list({ page: 1, pageSize: 100 }), []);
  const agents = (agentsReq.data?.items ?? []) as UserView[];

  const [fromAgentId, setFromAgentId] = useState('');
  const [toAgentId, setToAgentId] = useState('');
  const [mode, setMode] = useState<Mode>('ALL');
  const [value, setValue] = useState('');
  const [preview, setPreview] = useState<ReassignPreviewView | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<string | null>(null);

  const previewReq = useMutation(api.clients.reassignPreview);
  const applyReq = useMutation(api.clients.reassign);

  const agentName = (id: string) => agents.find((a) => a.id === id)?.fullName ?? id;

  // The selection sent to the API. "One by one" previews ALL and the user ticks rows.
  const selection = (forApply: boolean): ReassignSelectionDto => {
    if (mode === 'PROVINCE') return { mode, value: value.trim() };
    if (mode === 'CITY') return { mode, value: value.trim() };
    if (mode === 'RECENT_ORDERS') return { mode, months: RECENT_MONTHS };
    if (mode === 'IDS' && forApply) return { mode, clientIds: [...ticked] };
    return { mode: 'ALL' };
  };

  const canPreview =
    Boolean(fromAgentId) && (mode === 'ALL' || mode === 'RECENT_ORDERS' || mode === 'IDS' || value.trim().length > 0);

  async function runPreview() {
    setDone(null);
    const dto: ReassignClientsPreviewDto = { fromAgentId, selection: selection(false) };
    try {
      const res = await previewReq.mutate(dto);
      setPreview(res);
      setTicked(new Set(mode === 'IDS' ? [] : res.clients.map((c) => c.id)));
    } catch {
      /* previewReq.error is surfaced below */
    }
  }

  const selectedCount = mode === 'IDS' ? ticked.size : (preview?.total ?? 0);
  const canApply = Boolean(preview) && selectedCount > 0 && Boolean(toAgentId) && toAgentId !== fromAgentId;

  async function apply() {
    if (!canApply) return;
    if (
      !window.confirm(
        t('confirmDialog', { count: selectedCount, from: agentName(fromAgentId), to: agentName(toAgentId) }),
      )
    ) {
      return;
    }
    try {
      const res = await applyReq.mutate({ fromAgentId, toAgentId, selection: selection(true) });
      setDone(t('done', { count: res.count, to: agentName(toAgentId) }));
      setPreview(null);
      setTicked(new Set());
    } catch {
      /* applyReq.error is surfaced below */
    }
  }

  const lastOrder = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const modeOptions = useMemo(
    () => [
      ['ALL', t('modeAll')],
      ['PROVINCE', t('modeProvince')],
      ['CITY', t('modeCity')],
      ['RECENT_ORDERS', t('modeRecent', { months: RECENT_MONTHS })],
      ['IDS', t('modeIds')],
    ] as const,
    [t],
  );

  return (
    <>
      <div
        className="row center between"
        style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: '#fff' }}
      >
        <div className="col">
          <div className="meta">{t('subtitle')}</div>
          <div className="h1">{t('heading')}</div>
        </div>
        <button type="button" className="btn" onClick={() => navigate('/admin/clients')}>
          {t('backBtn')}
        </button>
      </div>

      <div style={{ padding: '14px 22px', overflow: 'auto', flex: 1 }}>
        <div className="panel" style={{ padding: 18, maxWidth: 980 }}>
          {agentsReq.loading ? (
            <LoadingState label={t('previewLoading')} />
          ) : (
            <div className="col gap-12">
              <div className="row gap-12" style={{ flexWrap: 'wrap' }}>
                <label className="col gap-4" style={{ flex: '1 1 260px' }}>
                  <span className="label">{t('fromAgent')}</span>
                  <select className="input" value={fromAgentId} onChange={(e) => { setFromAgentId(e.target.value); setPreview(null); }}>
                    <option value="">{t('selectAgent')}</option>
                    {agents.map((a) => (
                      <option key={a.id} value={a.id}>{a.fullName} · {a.email}</option>
                    ))}
                  </select>
                </label>
                <label className="col gap-4" style={{ flex: '1 1 260px' }}>
                  <span className="label">{t('toAgent')}</span>
                  <select className="input" value={toAgentId} onChange={(e) => setToAgentId(e.target.value)}>
                    <option value="">{t('selectAgent')}</option>
                    {agents.filter((a) => a.id !== fromAgentId).map((a) => (
                      <option key={a.id} value={a.id}>{a.fullName} · {a.email}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="row gap-12" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <label className="col gap-4" style={{ flex: '1 1 260px' }}>
                  <span className="label">{t('selection')}</span>
                  <select className="input" value={mode} onChange={(e) => { setMode(e.target.value as Mode); setPreview(null); }}>
                    {modeOptions.map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                </label>
                {(mode === 'PROVINCE' || mode === 'CITY') && (
                  <label className="col gap-4" style={{ flex: '1 1 200px' }}>
                    <span className="label">{mode === 'PROVINCE' ? t('valueProvince') : t('valueCity')}</span>
                    <input className="input" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Madrid" />
                  </label>
                )}
                <button type="button" className="btn primary" disabled={!canPreview || previewReq.loading} onClick={() => void runPreview()}>
                  {previewReq.loading ? t('previewLoading') : t('previewBtn')}
                </button>
              </div>
              {previewReq.error && <ErrorState message={previewReq.error.message} />}
              {applyReq.error && <ErrorState message={applyReq.error.message} />}
              {toAgentId && toAgentId === fromAgentId && (
                <div className="meta" style={{ color: 'var(--red)', fontSize: 13 }}>{t('sameAgent')}</div>
              )}
              {done && (
                <div className="panel" style={{ padding: '10px 14px', background: 'var(--green-bg)', border: '1px solid var(--green-line)' }}>
                  <span className="meta" style={{ fontSize: 13, color: 'var(--green)' }}>{done}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {preview && (
          <div className="panel mt-16" style={{ maxWidth: 980 }}>
            <div className="row between center" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-2)' }}>
              <div className="col">
                <div className="h3">
                  {t(selectedCount === 1 ? 'previewCount_one' : 'previewCount_other', { count: selectedCount })}
                </div>
                <div className="meta" style={{ fontSize: 13 }}>{t('openOrdersHint')}</div>
              </div>
              <button type="button" className="btn primary" disabled={!canApply || applyReq.loading} onClick={() => void apply()}>
                {t('confirmBtn', { count: selectedCount })}
              </button>
            </div>
            {preview.clients.length === 0 ? (
              <div className="meta" style={{ padding: 16 }}>{t('previewEmpty')}</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    {mode === 'IDS' && <th></th>}
                    <th>{t('colClient')}</th>
                    <th>{t('colCity')}</th>
                    <th>{t('colProvince')}</th>
                    <th>{t('colLastOrder')}</th>
                    <th style={{ textAlign: 'right' }}>{t('colOpenOrders')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.clients.map((c) => (
                    <tr key={c.id}>
                      {mode === 'IDS' && (
                        <td>
                          <input
                            type="checkbox"
                            checked={ticked.has(c.id)}
                            onChange={(e) =>
                              setTicked((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(c.id);
                                else next.delete(c.id);
                                return next;
                              })
                            }
                          />
                        </td>
                      )}
                      <td style={{ fontWeight: 500 }}>{c.company}</td>
                      <td className="meta">{c.city ?? '—'}</td>
                      <td className="meta">{c.province ?? '—'}</td>
                      <td className="meta">{lastOrder(c.lastOrderAt)}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>{c.openOrders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </>
  );
}
